import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Delete, Edit, Save, Add, ExpandMore, ExpandLess } from '@mui/icons-material';
import api from '../../services/api';

/**
 * Custom hook for debouncing function calls
 * @param {Function} callback - Function to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {Function} Debounced function
 */
const useDebounce = (callback, delay) => {
  const timer = useRef();
  return (...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };
};

/**
 * React component for managing task templates and their items
 * @component
 * @returns {JSX.Element} ActionItemTemplateSettings component
 */
const ActionItemTemplateSettings = () => {
  const [templates, setTemplates] = useState([]);
  const [expandedTemplateId, setExpandedTemplateId] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [editingTemplateName, setEditingTemplateName] = useState('');
  const [editingTemplateDescription, setEditingTemplateDescription] = useState('');
  const [templateItems, setTemplateItems] = useState({});
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemSeq, setNewItemSeq] = useState('');
  const [newItemNotes, setNewItemNotes] = useState('');
  const [newItemTimeEstimate, setNewItemTimeEstimate] = useState('');
  const [newItemChecklist, setNewItemChecklist] = useState('');
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItemDesc, setEditingItemDesc] = useState('');
  const [editingItemSeq, setEditingItemSeq] = useState('');
  const [editingItemNotes, setEditingItemNotes] = useState('');
  const [editingItemTimeEstimate, setEditingItemTimeEstimate] = useState('');
  const [editingItemChecklist, setEditingItemChecklist] = useState('');
  const [checklistTemplates, setChecklistTemplates] = useState([]);
  
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: null, parentId: null });

  useEffect(() => {
    fetchTemplates();
    fetchChecklistTemplates();
  }, []);

  /**
   * Fetches all task templates from the API
   * @async
   * @returns {Promise<void>}
   */
  const fetchTemplates = async () => {
    try {
      const res = await api.get('action-item-templates/');
      setTemplates(res.data.payload);
    } catch {
      setTemplates([]);
    }
  };

  /**
   * Fetches all checklist templates from the API
   * @async
   * @returns {Promise<void>}
   */
  const fetchChecklistTemplates = async () => {
    try {
      const res = await api.get('checklist-template/');
      setChecklistTemplates(res.data.payload);
    } catch {
      setChecklistTemplates([]);
    }
  };

  /**
   * Fetches template items for a specific template
   * @async
   * @param {number} templateId - ID of the template to fetch items for
   * @returns {Promise<void>}
   */
  const fetchTemplateItems = async (templateId) => {
    try {
      const res = await api.get(`action-item-template-items/${templateId}`);
      setTemplateItems(prev => ({ ...prev, [templateId]: res.data.payload }));
    } catch {
      setTemplateItems(prev => ({ ...prev, [templateId]: [] }));
    }
  };

  /**
   * Creates a new template with debounced API call
   * @type {Function}
   */
  const debouncedCreateTemplate = useDebounce(async (name, description) => {
    if (!name.trim() || !description.trim()) return;
    try {
      const res = await api.post('action-item-templates/', {
        template_name: name,
        template_description: description
      });
      setTemplates(prev => [...prev, res.data]);
      setTemplateName('');
      setTemplateDescription('');
    } catch {}
  }, 1000);

  /**
   * Handles template input changes and triggers debounced creation
   * @param {Event} e - Input change event
   * @param {string} type - Type of input ('name' or 'desc')
   * @returns {void}
   */
  const handleTemplateInput = (e, type) => {
    if (type === 'name') {
      setTemplateName(e.target.value);
      if (e.target.value.trim() && templateDescription.trim()) {
        debouncedCreateTemplate(e.target.value, templateDescription);
      }
    } else {
      setTemplateDescription(e.target.value);
      if (templateName.trim() && e.target.value.trim()) {
        debouncedCreateTemplate(templateName, e.target.value);
      }
    }
  };

  /**
   * Handles expanding/collapsing a template to show its items
   * @param {number} templateId - ID of the template to expand/collapse
   * @returns {void}
   */
  const handleExpandTemplate = (templateId) => {
    setExpandedTemplateId(expandedTemplateId === templateId ? null : templateId);
    if (expandedTemplateId !== templateId) {
      fetchTemplateItems(templateId);
      setNewItemDesc('');
      setNewItemSeq('');
      setNewItemNotes('');
      setNewItemTimeEstimate('');
      setNewItemChecklist('');
      setEditingItemId(null);
    }
  };

  /**
   * Initiates editing mode for a template
   * @param {Object} template - Template object to edit
   * @returns {void}
   */
  const handleEditTemplate = (template) => {
    setEditingTemplateId(template.id);
    setEditingTemplateName(template.template_name);
    setEditingTemplateDescription(template.template_description);
  };

  /**
   * Saves template edits to the API
   * @async
   * @returns {Promise<void>}
   */
  const handleSaveTemplateEdit = async () => {
    try {
      await api.patch(`action-item-template/${editingTemplateId}`, {
        template_name: editingTemplateName,
        template_description: editingTemplateDescription
      });
      setTemplates(prev =>
        prev.map(tpl =>
          tpl.id === editingTemplateId
            ? { ...tpl, template_name: editingTemplateName, template_description: editingTemplateDescription }
            : tpl
        )
      );
      setEditingTemplateId(null);
    } catch {}
  };

  /**
   * Deletes a template from the API and updates state
   * @async
   * @param {number} id - ID of the template to delete
   * @returns {Promise<void>}
   */
  const handleDeleteTemplate = async (id) => {
    try {
      await api.delete(`action-item-template/${id}`);
      setTemplates(prev => prev.filter(tpl => tpl.id !== id));
      setTemplateItems(prev => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      if (expandedTemplateId === id) setExpandedTemplateId(null);
    } catch {}
    setDeleteDialog({ open: false, type: '', id: null, parentId: null });
  };

  /**
   * Adds a new item to a template
   * @async
   * @param {number} templateId - ID of the template to add item to
   * @returns {Promise<void>}
   */
  const handleAddItem = async (templateId) => {
    if (!newItemDesc.trim()) return;
    try {
      const res = await api.post('action-item-template-items/', {
        template: templateId,
        description: newItemDesc,
        notes: newItemNotes,
        seq: newItemSeq ? parseInt(newItemSeq) : 0,
        ai_time_estimate: newItemTimeEstimate ? parseFloat(newItemTimeEstimate) : 0.0,
        checklist: newItemChecklist || null
      });
      setTemplateItems(prev => ({
        ...prev,
        [templateId]: [...(prev[templateId] || []), res.data.payload]
      }));
      setNewItemDesc('');      setNewItemSeq('');
      setNewItemNotes('');
      setNewItemTimeEstimate('');
      setNewItemChecklist('');
    } catch {}
  };

  /**
   * Initiates editing mode for a template item
   * @param {Object} item - Item object to edit
   * @returns {void}
   */
  const handleEditItem = (item) => {
    setEditingItemId(item.id);
    setEditingItemDesc(item.description);
    setEditingItemSeq(item.seq);
    setEditingItemNotes(item.notes || '');
    setEditingItemTimeEstimate(item.ai_time_estimate || '');
    setEditingItemChecklist(item.checklist || '');
  };

  /**
   * Saves item edits to the API
   * @async
   * @param {number} templateId - ID of the template containing the item
   * @returns {Promise<void>}
   */
  const handleSaveItemEdit = async (templateId) => {
    try {
      await api.patch(`action-item-template-items/${editingItemId}`, {
        description: editingItemDesc,
        notes: editingItemNotes,
        seq: editingItemSeq ? parseInt(editingItemSeq) : 0,
        ai_time_estimate: editingItemTimeEstimate ? parseFloat(editingItemTimeEstimate) : 0.0,
        checklist: editingItemChecklist || null
      });
      setTemplateItems(prev => ({
        ...prev,
        [templateId]: prev[templateId].map(item =>
          item.id === editingItemId
            ? { 
                ...item, 
                description: editingItemDesc, 
                notes: editingItemNotes,
                seq: editingItemSeq,
                ai_time_estimate: editingItemTimeEstimate,
                checklist: editingItemChecklist
              }
            : item
        )
      }));
      setEditingItemId(null);
    } catch {}
  };

  /**
   * Deletes an item from a template
   * @async
   * @param {number} templateId - ID of the template containing the item
   * @param {number} id - ID of the item to delete
   * @returns {Promise<void>}
   */
  const handleDeleteItem = async (templateId, id) => {
    try {
      await api.delete(`action-item-template-items/${id}`);
      setTemplateItems(prev => ({
        ...prev,
        [templateId]: prev[templateId].filter(item => item.id !== id)
      }));
    } catch {}
    setDeleteDialog({ open: false, type: '', id: null, parentId: null });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Task Templates
      </Typography>      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Create New Template
        </Typography>
        <Grid container spacing={2}>
          <Grid size={6}>
            <TextField
              label="Template Name"
              value={templateName}
              onChange={e => handleTemplateInput(e, 'name')}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <TextField
              label="Template Description"
              value={templateDescription}
              onChange={e => handleTemplateInput(e, 'desc')}
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>
      <List>
        {templates.map(template => (
          <React.Fragment key={template.id}>
            <ListItem
              secondaryAction={
                <>
                  <IconButton onClick={() => handleEditTemplate(template)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => setDeleteDialog({ open: true, type: 'template', id: template.id })}>
                    <Delete />
                  </IconButton>
                  <IconButton onClick={() => handleExpandTemplate(template.id)}>
                    {expandedTemplateId === template.id ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </>
              }
            >
              <ListItemText
                primary={                  editingTemplateId === template.id ? (
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={4}>
                        <TextField
                          value={editingTemplateName}
                          onChange={e => setEditingTemplateName(e.target.value)}
                          size="small"
                          label="Template Name"
                          fullWidth
                        />
                      </Grid>
                      <Grid size={6}>
                        <TextField
                          value={editingTemplateDescription}
                          onChange={e => setEditingTemplateDescription(e.target.value)}
                          size="small"
                          label="Description"
                          fullWidth
                        />
                      </Grid>
                      <Grid size={2}>
                        <IconButton onClick={handleSaveTemplateEdit} color="primary">
                          <Save />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ) : (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {template.template_name}
                      </Typography>                      <Typography variant="body2" color="text.secondary">
                        {template.template_description}
                      </Typography>
                    </>
                  )
                }
              />
            </ListItem>
            <Collapse in={expandedTemplateId === template.id} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 4, pb: 2 }}>
                <List>
                  {(templateItems[template.id] || []).map(item => (
                    <ListItem
                      key={item.id}
                      secondaryAction={
                        <>
                          <IconButton onClick={() => handleEditItem(item)}>
                            <Edit />
                          </IconButton>
                          <IconButton onClick={() => setDeleteDialog({ open: true, type: 'item', id: item.id, parentId: template.id })}>
                            <Delete />
                          </IconButton>
                        </>
                      }
                    >
                      <ListItemText                        primary={                          editingItemId === item.id ? (
                            <Grid container spacing={2} sx={{ width: '100%' }}>
                              <Grid size={8}>
                                <TextField
                                  value={editingItemDesc}
                                  onChange={e => setEditingItemDesc(e.target.value)}
                                  size="small"
                                  label="Description"
                                  fullWidth
                                />
                              </Grid>
                              <Grid size={4}>
                                <TextField
                                  value={editingItemSeq}
                                  onChange={e => setEditingItemSeq(e.target.value)}
                                  size="small"
                                  label="Sequence"
                                  type="number"
                                  fullWidth
                                />
                              </Grid>
                              <Grid size={12}>
                                <TextField
                                  value={editingItemNotes}
                                  onChange={e => setEditingItemNotes(e.target.value)}
                                  size="small"
                                  label="Notes"
                                  multiline
                                  rows={2}
                                  fullWidth
                                />
                              </Grid>
                              <Grid size={6}>
                                <TextField
                                  value={editingItemTimeEstimate}
                                  onChange={e => setEditingItemTimeEstimate(e.target.value)}
                                  size="small"
                                  label="Time Estimate (hrs)"
                                  type="number"
                                  inputProps={{ step: '0.01' }}
                                  fullWidth
                                />
                              </Grid>
                              <Grid size={6}>
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Checklist Template</InputLabel>
                                  <Select
                                    value={editingItemChecklist}
                                    onChange={e => setEditingItemChecklist(e.target.value)}
                                    label="Checklist Template"
                                  >
                                    <MenuItem value="">None</MenuItem>
                                    {checklistTemplates.map(template => (
                                      <MenuItem key={template.id} value={template.id}>
                                        {template.template_name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid size={12} sx={{ textAlign: 'right' }}>
                                <IconButton onClick={() => handleSaveItemEdit(template.id)} color="primary">
                                  <Save />
                                </IconButton>
                              </Grid>
                            </Grid>
                          ) : (
                            <Box>
                              <Typography variant="body1">{item.description}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                Seq: {item.seq}
                              </Typography>
                              {item.notes && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  Notes: {item.notes}
                                </Typography>
                              )}
                              <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                {item.ai_time_estimate > 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    Time: {item.ai_time_estimate}h
                                  </Typography>
                                )}
                                {item.checklist && (
                                  <Typography variant="caption" color="text.secondary">
                                    Checklist: {checklistTemplates.find(t => t.id === item.checklist)?.template_name || 'Unknown'}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )
                        }
                      />
                    </ListItem>
                  ))}                  <ListItem>
                    <Paper sx={{ p: 2, width: '100%' }} elevation={1}>
                      <Typography variant="subtitle2" gutterBottom>
                        Add New Item
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid size={8}>
                          <TextField
                            value={newItemDesc}
                            onChange={e => setNewItemDesc(e.target.value)}
                            size="small"
                            label="Description"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={4}>
                          <TextField
                            value={newItemSeq}
                            onChange={e => setNewItemSeq(e.target.value)}
                            size="small"
                            label="Sequence"
                            type="number"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            value={newItemNotes}
                            onChange={e => setNewItemNotes(e.target.value)}
                            size="small"
                            label="Notes"
                            multiline
                            rows={2}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={6}>
                          <TextField
                            value={newItemTimeEstimate}
                            onChange={e => setNewItemTimeEstimate(e.target.value)}
                            size="small"
                            label="Time Estimate (hrs)"
                            type="number"
                            inputProps={{ step: '0.01' }}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={6}>
                          <FormControl size="small" fullWidth>
                            <InputLabel>Checklist Template</InputLabel>
                            <Select
                              value={newItemChecklist}
                              onChange={e => setNewItemChecklist(e.target.value)}
                              label="Checklist Template"
                            >
                              <MenuItem value="">None</MenuItem>
                              {checklistTemplates.map(template => (
                                <MenuItem key={template.id} value={template.id}>
                                  {template.template_name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={12} sx={{ textAlign: 'right' }}>
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleAddItem(template.id)}
                            color="primary"
                          >
                            Add Item
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </ListItem>
                </List>
              </Box>
            </Collapse>
            <Divider />
          </React.Fragment>
        ))}
      </List>
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, type: '', id: null, parentId: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteDialog.type === 'template'
              ? 'Are you sure you want to delete this template?'
              : 'Are you sure you want to delete this item?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: '', id: null, parentId: null })}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              if (deleteDialog.type === 'template') {
                handleDeleteTemplate(deleteDialog.id);
              } else {
                handleDeleteItem(deleteDialog.parentId, deleteDialog.id);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActionItemTemplateSettings;
